// transport.js — single fetch wrapper for all calendar providers.
//
// Responsibilities
//   • Route requests:
//       - Browser web   → through the Supabase `caldav-relay` edge function
//                         for CalDAV (CORS), direct for OAuth providers
//                         (Google/Microsoft both ship CORS headers).
//       - Electron      → always direct (Node-side fetch, no CORS).
//       - Capacitor iOS → always direct (native fetch, no CORS).
//   • Inject auth (bearer for OAuth, basic for CalDAV).
//   • Handle 401 → token refresh via the token-manager (one in-flight refresh
//     per accountId, callers await the same promise). This is the *fallback*
//     path: the token-manager's getValidAccessToken refreshes PROACTIVELY in
//     the load path when a token is within its skew window, so a healthy
//     in-flight request rarely has to spend a forced retry here. The 401 hook
//     still covers tokens revoked or expired server-side ahead of their
//     advertised expires_at.
//   • Exponential backoff on 429 / 5xx, capped at 3 attempts.
//   • Normalise common failure shapes to CalendarProviderError codes so
//     providers can stay short and declarative.
//
// Providers never call fetch() directly — they call Transport.request(...).

import { CalendarProviderError, ERROR_CODES } from './calendar-provider.js';

// Allow-listed CalDAV hosts. The relay edge function enforces this
// server-side too — the client check is just to fail fast before the
// network round-trip.
const DEFAULT_CALDAV_ALLOWLIST = [
  'caldav.icloud.com',
  // iCloud shards principal discovery onto per-user hosts (p<NN>-caldav.icloud.com),
  // so every request after the initial caldav.icloud.com PROPFIND targets a shard.
  '*.icloud.com',
  'caldav.fastmail.com',
  'apidata.googleusercontent.com', // Google CalDAV (legacy)
  // Self-hosted setups: users can pre-register their own hosts in
  // calendar_relay_hosts (RLS-protected, per-user). The wildcard below
  // is *only* a client-side fast-fail hint; the edge function does the
  // authoritative check via the per-user table.
  '*.nextcloud.com',
];

// Detect host environment exactly the way platform.js does, but with
// graceful fallbacks for the Jest/jsdom test environment.
/** @returns {{ isWeb: boolean, isElectron: boolean, isCapacitor: boolean }} */
function _detectHost() {
  if (typeof window === 'undefined') return { isWeb: false, isElectron: false, isCapacitor: false };
  const P = window.Platform || {};
  return {
    isWeb: !!P.isWeb,
    isElectron: !!P.isElectron,
    isCapacitor: !!P.isCapacitor,
  };
}

// The Supabase relay base URL is configured per environment. On test or
// when unset we throw a clear error rather than silently sending the
// request to a guessed host.
/** @returns {string | null} the Supabase edge-functions base URL, or null. */
function _relayBase() {
  if (typeof window === 'undefined') return null;
  if (window.SUPABASE_FUNCTIONS_URL) return window.SUPABASE_FUNCTIONS_URL;
  // window.SUPABASE_URL isn't injected in every host (e.g. web served from the
  // bundled config), so fall back to the PowerSync config that carries the same
  // Supabase project URL — it's present wherever sync is configured. Without this
  // the base resolves null, useRelay flips false, and web CalDAV silently routes
  // DIRECT to the provider → CORS → "Failed to fetch".
  const supabaseUrl = window.SUPABASE_URL || (window.POWERSYNC_CONFIG as any)?.supabaseUrl;
  if (supabaseUrl) {
    // Standard pattern: https://<ref>.supabase.co/functions/v1/
    return `${supabaseUrl.replace(/\/$/, '')}/functions/v1`;
  }
  return null;
}

/**
 * @param {string} url
 * @param {string[]} [allowlist]
 * @returns {boolean}
 */
function _isAllowedCaldavHost(url: any, allowlist: any) {
  try {
    const u = new URL(url);
    const list = allowlist || DEFAULT_CALDAV_ALLOWLIST;
    // Support wildcard suffixes (`*.nextcloud.com`) for self-hosted
    // setups added via the panel's "custom server" field.
    for (const entry of list) {
      if (entry === u.host) return true;
      if (entry.startsWith('*.') && u.host.endsWith(entry.slice(1))) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Map a fetch Response status to a CalendarProviderError code. Returns
// null when the response is successful (callers check resp.ok instead).
/**
 * @param {number} status
 * @returns {string | null} a CalendarProviderError code, or null when ok.
 */
function _classifyStatus(status: any) {
  if (status === 401) return ERROR_CODES.AUTH_REQUIRED;
  // 403 is NOT an auth-token problem — a refresh won't fix it. Default to
  // FORBIDDEN (permanent); the request loop upgrades a throttle-reason 403 to
  // RATE_LIMITED after peeking the body.
  if (status === 403) return ERROR_CODES.FORBIDDEN;
  if (status === 404 || status === 410) return ERROR_CODES.NOT_FOUND;
  if (status === 409) return ERROR_CODES.CONFLICT;
  if (status === 412) return ERROR_CODES.PRECONDITION_FAILED;
  if (status === 429) return ERROR_CODES.RATE_LIMITED;
  if (status >= 500 && status < 600) return ERROR_CODES.TRANSIENT;
  return null;
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function _sleep(ms: any) {
  return new Promise((r) => setTimeout(r, ms));
}

// ── In-flight refresh dedup ──
// Mirrors the pattern from calendar-sync.js's `_calendarSyncPromise`:
// multiple parallel 401s for the same accountId collapse onto a single
// refresh promise so we never burn through the IDP rate limit during a
// sync storm.
const _refreshInflight: Map<string, Promise<any>> = new Map();
/**
 * @param {string} accountId
 * @param {(accountId: string) => Promise<any>} [refreshFn]
 * @returns {Promise<any>}
 */
function _refreshOnce(accountId: any, refreshFn: any) {
  if (!accountId || !refreshFn) return Promise.resolve(null);
  if (_refreshInflight.has(accountId)) return _refreshInflight.get(accountId) as Promise<any>;
  const p = Promise.resolve()
    .then(() => refreshFn(accountId))
    .finally(() => _refreshInflight.delete(accountId));
  _refreshInflight.set(accountId, p);
  return p;
}

// ── Auth header builders ──
/**
 * @param {string} [token]
 * @returns {Record<string, string>}
 */
function _bearer(token: any) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * @param {string} user
 * @param {string} [pass]
 * @returns {Record<string, string>}
 */
function _basic(user: any, pass: any) {
  if (!user) return {};
  const raw = `${user}:${pass || ''}`;
  // Node's Buffer isn't in the browser typelib; reach it off the global so
  // the bare identifier doesn't need @types/node. Runtime is unchanged.
  const _buf = (globalThis as any).Buffer;
  let b64: any;
  if (typeof btoa === 'function') b64 = btoa(raw);
  else if (typeof _buf !== 'undefined') b64 = _buf.from(raw, 'utf8').toString('base64');
  else throw new CalendarProviderError(ERROR_CODES.TRANSIENT, 'no base64 encoder available');
  return { Authorization: `Basic ${b64}` };
}

// ── Main entry point ──
// opts: {
//   url, method='GET', headers={}, body?,
//   accountId,                       — for refresh dedup + relay JWT
//   credentials: { kind: 'bearer'|'basic'|'none', token?, user?, pass? },
//   refresh?: async (accountId) => { token } | null,  — fallback, called
//                                    once on 401 (proactive refresh lives in
//                                    token-manager.getValidAccessToken)
//   route?: 'auto'|'direct'|'relay'  — override host-based routing
//   relayAllowlist?: string[]        — extends DEFAULT_CALDAV_ALLOWLIST
//   timeoutMs?: number,              — default 30s
//   maxAttempts?: number,            — default 3
//   useCaldav?: boolean              — true for CalDAV/iCloud; toggles relay
// }
/**
 * @param {any} opts - see the option contract documented above.
 * @returns {Promise<Response>}
 */
async function request(opts: any) {
  if (!opts?.url) throw new TypeError('Transport.request: opts.url required');
  const {
    url,
    method = 'GET',
    headers = {},
    body,
    accountId,
    credentials,
    refresh,
    route = 'auto',
    relayAllowlist,
    timeoutMs = 30000,
    maxAttempts = 3,
    useCaldav = false,
  } = opts;

  const host = _detectHost();
  // Routing decision: relay only on browser web, only for CalDAV, only if
  // a Supabase functions URL is configured. Direct everywhere else.
  let useRelay: any;
  if (route === 'relay') useRelay = true;
  else if (route === 'direct') useRelay = false;
  else useRelay = !!(useCaldav && host.isWeb && _relayBase());

  if (useRelay && !_isAllowedCaldavHost(url, relayAllowlist)) {
    // The relay edge function is the AUTHORITATIVE allow-list: it merges the
    // defaults with the caller's per-user `calendar_relay_hosts` table, which
    // the client can't see. A host outside the static client list may still be
    // a legitimately-registered self-hosted CalDAV server, so don't hard-fail
    // here — let the relay accept it (or return 403 → FORBIDDEN). Hard-failing
    // here broke the "Other CalDAV (Nextcloud / self-hosted)" feature on web.
    console.warn('[transport] CalDAV host not in client allow-list; deferring to relay:', url);
  }

  let attempt = 0;
  let didRefresh = false;
  let lastErr = null;

  while (attempt < maxAttempts) {
    attempt += 1;
    const finalHeaders = { ...headers };
    // Auth header: rebuilt each attempt because a refresh may have
    // swapped the token between attempts.
    if (credentials && credentials.kind === 'bearer') {
      Object.assign(finalHeaders, _bearer(credentials.token));
    } else if (credentials && credentials.kind === 'basic') {
      Object.assign(finalHeaders, _basic(credentials.user, credentials.pass));
    }

    // Typed as Response (not any) so resp.text() stays string-typed and the
    // bodyText narrowing below holds — _direct/_viaRelay both resolve to a fetch
    // Response.
    let resp: Response;
    try {
      resp = useRelay
        ? await _viaRelay({ url, method, headers: finalHeaders, body, timeoutMs })
        : await _direct({ url, method, headers: finalHeaders, body, timeoutMs });
    } catch (e: any) {
      // Network / abort → OFFLINE so the outbox picks it up.
      lastErr = new CalendarProviderError(ERROR_CODES.OFFLINE, e?.message || 'network error', {
        cause: e,
      });
      // Retry transient network errors with backoff, but not abort.
      if (attempt < maxAttempts && e?.name !== 'AbortError') {
        await _sleep(Math.min(1000 * 2 ** (attempt - 1), 8000));
        continue;
      }
      throw lastErr;
    }

    if (resp.ok) return resp;

    let code = _classifyStatus(resp.status);
    // The response body is consumable once; read it lazily and remember it so
    // the throw at the end doesn't try to re-read a used stream.
    let bodyText: string | undefined;

    // Google/Microsoft signal throttling/quota with a 403 carrying a
    // machine-readable reason (a real permission denial does not). Peek the
    // body to route a throttle 403 to RATE_LIMITED (backoff) instead of
    // FORBIDDEN (permanent), so a rate-limited account isn't dropped for the
    // cycle. Reading here is safe — a 403 is terminal for this attempt.
    if (resp.status === 403) {
      try {
        bodyText = await resp.text();
      } catch {
        bodyText = '';
      }
      if (
        /rateLimitExceeded|userRateLimitExceeded|dailyLimitExceeded|quotaExceeded|ApplicationThrottled/i.test(
          bodyText
        )
      ) {
        code = ERROR_CODES.RATE_LIMITED;
      }
    }

    // One-shot refresh on auth failure (401 only — a 403 is not a token issue).
    if (code === ERROR_CODES.AUTH_REQUIRED && !didRefresh && refresh) {
      didRefresh = true;
      const refreshed = await _refreshOnce(accountId, refresh).catch(() => null as any);
      if (refreshed?.token && credentials) credentials.token = refreshed.token;
      continue; // retry without consuming the attempt counter
    }

    // Backoff on rate-limited / transient errors.
    if (
      (code === ERROR_CODES.RATE_LIMITED || code === ERROR_CODES.TRANSIENT) &&
      attempt < maxAttempts
    ) {
      const retryAfter = Number(resp.headers?.get?.('Retry-After'));
      const wait =
        Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : Math.min(1000 * 2 ** (attempt - 1), 8000);
      await _sleep(wait);
      continue;
    }

    // Surface the error. Callers may consult `.status` and `.code`.
    if (bodyText === undefined) {
      try {
        bodyText = await resp.text();
      } catch {
        bodyText = '';
      }
    }
    throw new CalendarProviderError(
      code || ERROR_CODES.TRANSIENT,
      `${method} ${url} → ${resp.status} ${resp.statusText || ''} ${bodyText.slice(0, 200)}`,
      { status: resp.status }
    );
  }

  throw lastErr || new CalendarProviderError(ERROR_CODES.TRANSIENT, 'exhausted attempts');
}

// ── Underlying fetches ──
/**
 * @param {any} opts - { url, method, headers, body?, timeoutMs }
 * @returns {Promise<Response>}
 */
async function _direct({ url, method, headers, body, timeoutMs }: any) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl && setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const init: RequestInit = { method, headers };
    if (body != null) init.body = body;
    if (ctrl) init.signal = ctrl.signal;
    return await fetch(url, init);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * @param {any} opts - { url, method, headers, body?, timeoutMs }
 * @returns {Promise<Response>}
 */
async function _viaRelay({ url, method, headers, body, timeoutMs }: any) {
  const base = _relayBase();
  if (!base)
    throw new Error('relay not configured (window.SUPABASE_URL or SUPABASE_FUNCTIONS_URL)');
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl && setTimeout(() => ctrl.abort(), timeoutMs);
  // Carry the user's Supabase JWT so the edge function can authn the caller.
  const jwt = window.WorkspaceSessionVault?.getCurrentAccessToken // runtime — CJS compat required for tests
    ? await window.WorkspaceSessionVault.getCurrentAccessToken().catch(() => null as any)
    : null;
  const relayHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
  if (jwt) relayHeaders.Authorization = `Bearer ${jwt}`;
  const payload = JSON.stringify({ url, method, headers, body });
  try {
    return await fetch(`${base}/caldav-relay`, {
      method: 'POST',
      headers: relayHeaders,
      body: payload,
      signal: ctrl ? ctrl.signal : undefined,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// ── Export ──
// Phase 4.7: real ESM. `CalendarTransport` is the aggregate the schedule
// modules destructure (`Transport.request(...)`); siblings import it directly.
export const CalendarTransport = {
  request,
  _isAllowedCaldavHost, // exposed for tests
  _classifyStatus, // exposed for tests
  _refreshInflight, // exposed for tests
  DEFAULT_CALDAV_ALLOWLIST,
};
